import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { ChartExportDataRequest } from "@/Patient/Models/GrowthCharts/ChartExportDataRequest";
import { ShareableGrowthChartModel } from "@/Patient/Models/GrowthCharts/ShareableGrowthChartModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientGrowthChartsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.GrowthCharts.SetPatientBornPrematureData, this.setPatientBornPrematureData);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.SetStartDateFilter, this.setStartDateFilter);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.SetEndDateFilter, this.setEndDateFilter);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.SetUnitFilter, this.setUnitFilter);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.ExportPatientCharts, this.exportPatientCharts);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.SetSelectedCharts, this.setSelectedCharts);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.PortalClick, this.openSendToPortalPopup);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.PortalSendClicked, this.onPortalSendClicked);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.PortalStatusCancel, this.onPortalCancelClicked);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.PortalRevokedClicked, this.onPortalRevokedClicked);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.LoadGrowthChartSharedItem, this.loadGrowthChartSharedItem);
        this.RegisterOperation(LocalPatientOperations.GrowthCharts.PortalGrowthChartMessageChecked, this.onPortalGrowthChartMessageChecked);
    }

    private setPatientBornPrematureData(request: LocalEvent, store: PatientStore): void {
        let isBornPremature = request.Get<boolean>("isBornPremature");
        let bornPrematureWeeks = request.Get<number>("bornPrematureWeeks");

        store.ActionsGrowthCharts.updatePatientBornPrematureData(request.PatientId, isBornPremature, bornPrematureWeeks);
    }

    private setStartDateFilter(request: LocalEvent, store: PatientStore): void {
        const date = request.Get<Date>("date");
        let model = store.GrowthChartUiModels[request.PatientId];

        if (model) {
            model = model.set("startDateFilter", date);
            store.GrowthChartUiModels[request.PatientId] = model;
        }
    }

    private setEndDateFilter(request: LocalEvent, store: PatientStore): void {
        const date = request.Get<Date>("date");
        let model = store.GrowthChartUiModels[request.PatientId];

        if (model) {
            model = model.set("endDateFilter", date);
            store.GrowthChartUiModels[request.PatientId] = model;
        }
    }

    private setUnitFilter(request: LocalEvent, store: PatientStore): void {
        const unit = request.Get<UnitSystem>("unit");
        const uiModel = store.GrowthChartUiModels[request.PatientId];

        if (!uiModel) {
            return;
        }

        store.GrowthChartUiModels[request.PatientId] = uiModel.set("unit", unit);
    }

    private exportPatientCharts(request: LocalEvent, store: PatientStore): void {
        const exportRequest = request.Get<ChartExportDataRequest>("exportRequest");
        store.ActionsGrowthCharts.exportPatientCharts(exportRequest);
    }

    private setSelectedCharts(request: LocalEvent, store: PatientStore): void {
        const selectedCharts = request.Get<{ [chartId: string]: boolean }>("selectedCharts");
        const uiModel = store.GrowthChartUiModels[request.PatientId];

        if (!uiModel) {
            return;
        }

        store.GrowthChartUiModels[request.PatientId] = uiModel.set("selectedCharts", selectedCharts);
    }

    private openSendToPortalPopup(request: LocalEvent, store: PatientStore): void {
        const doc = request.Get<ShareableGrowthChartModel>("doc");
        const chartData = request.Get<ChartExportDataRequest>("chartData");

        if (doc != null) {
            doc.setSharedItemMessagesSelectStatus();
            this.transformChartToSharedItems(doc, store, request.PatientId);

            store.GrowthChartUiModels[request.PatientId] = store.GrowthChartUiModels[request.PatientId].set("isPortalPopupOpen", true);
            store.GrowthChartUiModels[request.PatientId] = store.GrowthChartUiModels[request.PatientId].set("exportPatientChartData", chartData);
        }
    }

    private onPortalSendClicked(request: LocalEvent, store: PatientStore): void {
        const subject = request.Get<string>("subject");
        const messageText = request.Get<string>("messageText");
        const objectId = request.Get<string>("attachmentId");
        const includeDocument = request.Get<boolean>("includeDocument");
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let chartSyncModel = store.GrowthChartUiModels[request.PatientId].get("portalLinkedItemModel");

        let portalMessages: SharedItemMessage[] = [];
        if (chartSyncModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(chartSyncModel);
        }

        const sharedItemId: string = chartSyncModel.get("LinkedItemModel").getSharedItemId();

        const hasTextMessage = !IsNullOrWhiteSpace(messageText);
        const patientId = patientStore.getCurrentPatientContactId();

        // create new message if user has entered text in message textbox
        if (hasTextMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        const sharedItem = new SharedItem({
            SharedItemId: sharedItemId,
            Subject: subject,
            SharedItemType: PortalLinkedItemType.GrowthChart,
            PatientContactId: patientId,
            Messages: portalMessages,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        });

        store.ActionsGrowthCharts.sendChartToPortal(
            objectId,
            sharedItem,
            includeDocument
        );

        chartSyncModel = chartSyncModel.set("IsSending", true);
        store.GrowthChartUiModels[request.PatientId] = store.GrowthChartUiModels[request.PatientId].set("portalLinkedItemModel", chartSyncModel);
    }

    private onPortalCancelClicked(request: LocalEvent, store: PatientStore): void {
        let docSyncModel = store.GrowthChartUiModels[request.PatientId].get("portalLinkedItemModel");
        docSyncModel = PatientHelper.clearPortalLinkItemStates(docSyncModel);
        store.GrowthChartUiModels[request.PatientId] = store.GrowthChartUiModels[request.PatientId]
            .set("portalLinkedItemModel", docSyncModel)
            .set("isPortalPopupOpen", false);
    }

    private onPortalRevokedClicked(request: LocalEvent, store: PatientStore): void {
        const messageIdsToRevoke: number[] = [];
        const model = store.GrowthChartUiModels[request.PatientId].get("portalLinkedItemModel");

        if (model.get("LinkedItemMessages") != null) {
            let allPortalMessages = model.get("LinkedItemMessages");

            // revoke only pending messages
            allPortalMessages.map(x => {
                if (x.Status === PortalSyncStatus.Pending ||
                    x.Status === PortalSyncStatus.Viewed ||
                    x.Status === PortalSyncStatus.Error ||
                    x.Status === PortalSyncStatus.Unknown) {
                    messageIdsToRevoke.push(+x.SharedItemMessageId);
                }
            });
        }

        const sharedItemId: string = model.get("LinkedItemModel").getSharedItemId();
        store.ActionsGrowthCharts.RevokePortalGrowthChart(sharedItemId, messageIdsToRevoke, sessionStore.userId);
    }

    private loadGrowthChartSharedItem(request: LocalEvent, store: PatientStore): void {
        const patientContactId = request.Get<string>("patientContactId");
        store.ActionsGrowthCharts.loadGrowthChartSharedItem(patientContactId);
    }

    private onPortalGrowthChartMessageChecked(request: LocalEvent, store: PatientStore): void {
        const checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let model = store.GrowthChartUiModels[request.PatientId].get("portalLinkedItemModel");
            model = PatientHelper.handlePortalItemCheck(model, checkedItem);
            store.GrowthChartUiModels[request.PatientId] = store.GrowthChartUiModels[request.PatientId].set("portalLinkedItemModel", model);
        }
    }

    private transformChartToSharedItems(doc: ShareableGrowthChartModel, store: PatientStore, patientId: string): void {
        let linkedItemModel = store.GrowthChartUiModels[patientId].get("portalLinkedItemModel")
            .set("SendButtonText", cacheStore.GetString("SendToPatientPortal"))
            .set("LinkedItemMessages", Immutable.List(doc.getSharedItemMessages()))
            .set("LinkedItemType", doc.getPortalLinkedItemType())
            .set("LinkedItemId", doc.getSharedItemId())
            .set("LinkedItemModel", doc)
            .set("SharedItem", doc.SharedItem)
            .set("DefaultSubject", doc.getDefaultSubject())
            .set("ErrorMessage", null)
            .set("RegistrationStatus", store.getCurrentPatient().IndividualResponse.PortalSyncState)
            .set("PatientContactId", store.getCurrentPatientContactId());

        store.GrowthChartUiModels[patientId] = store.GrowthChartUiModels[patientId].set("portalLinkedItemModel", linkedItemModel);
    }
}

export { PatientGrowthChartsHandler };
